import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Drag to reorder a list.
//
// Order is meaningful in a recipe — it usually follows the method, or groups
// things by where they sit on the bench. Getting it wrong on the first pass
// is normal, and the only fix before this was deleting the row and retyping
// it.
//
// Reordering only: the rows stay in one list and nothing moves between
// containers. Moving a thing from one container to ANOTHER — a meal to a
// different day — is a different operation with different failure modes (a
// server round-trip that can fail, and a revert when it does), so it gets
// its own controller rather than a mode flag on this one.
//
// Nothing is persisted here. The list belongs to a form, so a drop reorders
// the DOM and rewrites the position fields; the change is saved when the form
// is, along with everything else that was edited. That also makes a drag
// undoable by simply not saving.
export default class extends Controller {
  static targets = ["item"]
  static values = {
    handle: String,
    // A row that OWNS the rows after it, until the next one of its kind — a
    // group heading and the ingredients under it. Dragging one takes its
    // members along.
    groupSelector: String
  }

  connect() {
    this.sortable = Sortable.create(this.element, {
      animation: 150,
      // Drag from the handle alone. The rows are full of comboboxes, number
      // fields and buttons: making the whole row draggable would mean every
      // attempt to select text or open a dropdown started a drag instead.
      handle: this.handleValue || undefined,
      draggable: "[data-sortable-target='item']",
      // Classes rather than inline styles, so the look lives in the
      // stylesheet with everything else.
      ghostClass: "is-drop-target",
      chosenClass: "is-grabbed",
      dragClass: "is-dragging",
      // Long-press on touch before a drag begins, so scrolling the page still
      // works — without it every swipe that starts on a row drags it.
      delay: 200,
      delayOnTouchOnly: true,
      // Remember what a dragged heading owns BEFORE the drop moves it — after
      // the move, "the rows following it" are its new neighbours, not its
      // members.
      onStart: (event) => { this.members = this.membersOf(event.item) },
      onEnd: (event) => this.dropped(event)
    })
  }

  // The rows a heading owns: everything after it up to the next heading.
  // Empty for an ordinary row, and for a heading with nothing under it.
  membersOf(row) {
    if (!this.groupSelectorValue || !row.matches(this.groupSelectorValue)) return []

    const members = []
    let next = row.nextElementSibling

    while (next && !next.matches(this.groupSelectorValue)) {
      if (next.matches("[data-sortable-target='item']")) members.push(next)
      next = next.nextElementSibling
    }

    return members
  }

  disconnect() {
    this.sortable?.destroy()
    this.sortable = null
  }

  // Hand off to whoever owns the meaning of the order. Dispatched as an event
  // rather than reaching for a sibling controller, so this file needs to know
  // nothing about ingredients, and a second list can use it unchanged.
  dropped(event) {
    // A heading's members follow it to wherever it landed.
    //
    // Without this, dragging a heading alone leaves its ingredients behind —
    // and since an ingredient belongs to whichever heading is above it, they
    // silently join the group before them. "For the marinade" moves to the
    // top and its four ingredients quietly become part of the sauce.
    let after = event?.item
    for (const member of this.members || []) {
      after.after(member)
      after = member
    }
    this.members = []

    this.dispatch("dropped", { bubbles: true })
  }
}
